/**
 * Admin/SA list of the public landing-page hero banners.
 *
 * ONE banner is live at a time — the hero shows a single banner, not a carousel — so switching one
 * on puts the current one away (enforced server-side in BannerController). Show-hide / delete here;
 * create + edit happen on the full-page editor (/admin/banners/new, /admin/banners/:id).
 * Gated by BANNER.MANAGE.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Plus, Pencil, Trash2, CircleDot, ImageOff, Megaphone } from 'lucide-react';
import { request } from '../../api/client';
import { Button, Switch, Modal, Tooltip, toast, cn } from '../../ui';

interface Banner {
  id: string;
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  showOverlay: boolean;
  ctaLabel?: string | null;
  ctaLink?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaLink?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export function BannersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState<Banner | null>(null);

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: () => request<Banner[]>({ url: '/banners' }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-banners'] });
    qc.invalidateQueries({ queryKey: ['public-banners'] });
  };

  const toggleMut = useConfirmedMutation((b) => ({ title: b.isActive ? "Hide this banner from the site?" : "Show this banner on the site?", content: b.isActive ? "It stays saved and you can put it back any time. The home page falls back to its default hero." : "It goes live on the home page straight away, and whichever banner is live now is put away — only one shows at a time.", okText: b.isActive ? "Yes, hide it" : "Yes, show it" }), {
    mutationFn: (b: Banner) =>
      request({
        url: `/banners/${b.id}`,
        method: 'PUT',
        data: {
          eyebrow: b.eyebrow || undefined,
          title: b.title,
          subtitle: b.subtitle || undefined,
          imageUrl: b.imageUrl || undefined,
          showOverlay: b.showOverlay,
          ctaLabel: b.ctaLabel || undefined,
          ctaLink: b.ctaLink || undefined,
          secondaryCtaLabel: b.secondaryCtaLabel || undefined,
          secondaryCtaLink: b.secondaryCtaLink || undefined,
          isActive: !b.isActive,
        },
      }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not update'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => request({ url: `/banners/${id}`, method: 'DELETE' }),
    onSuccess: () => { invalidate(); setConfirmDelete(null); toast.success('Banner deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not delete banner'),
  });

  /*
   * Reordering is gone with the carousel.
   *
   * `sortOrder` decided the ROTATION ORDER of a carousel that no longer rotates: with one banner
   * live, position changes nothing a visitor can see. The arrows (and their "the public landing
   * carousel follows this order" confirmation) promised an effect that would not happen. The
   * backend endpoint still exists and the column still orders this list; nothing calls it.
   */
  const live = banners.find((b) => b.isActive) ?? null;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 type-page-title text-content">
            <Megaphone className="size-6 text-brand-600" /> Landing Banners
          </h1>
          <p className="mt-1 text-sm text-muted">
            Design a banner and switch it on — it fills the top of the home page. Only one banner is live at a
            time, so switching one on puts the current one away.
          </p>
        </div>
        <Button variant="primary" icon={<Plus />} onClick={() => navigate('/admin/banners/new')}>Add banner</Button>
      </div>

      {/* One banner is live, so the useful fact is WHICH — a count of "2 active" was both
          impossible now and, when it did happen, silent about which one the site actually showed. */}
      <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
        <span className="rounded-pill border border-line bg-surface px-3 py-1 text-muted">
          {banners.length} {banners.length === 1 ? 'banner' : 'banners'}
        </span>
        {live ? (
          <span className="inline-flex max-w-full items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-3 py-1 font-semibold text-brand-700 dark:border-brand-900/50 dark:bg-brand-900/25 dark:text-brand-300">
            <CircleDot className="size-3.5 shrink-0" />
            <span className="truncate">Live: {live.title}</span>
          </span>
        ) : (
          <span className="rounded-pill border border-warning-300 bg-warning-50 px-3 py-1 font-semibold text-warning-700 dark:border-warning-500/40 dark:bg-warning-500/10 dark:text-harvest-300">
            No banner live — the home page shows its default
          </span>
        )}
      </div>

      <div className="mt-6 space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-card border border-line bg-surface shadow-e1" />
          ))
        ) : banners.length === 0 ? (
          <div className="grid place-items-center rounded-card border border-dashed border-line bg-surface py-20 text-center">
            <Megaphone className="size-12 text-subtle" />
            <p className="mt-4 type-section-title text-content">No banners yet</p>
            <p className="mt-1 text-sm text-muted">Add a banner to replace the home page's default hero.</p>
            <Button className="mt-5" variant="primary" icon={<Plus />} onClick={() => navigate('/admin/banners/new')}>Add banner</Button>
          </div>
        ) : (
          banners.map((b, i) => (
            <div
              key={b.id}
              className={cn(
                'flex items-center gap-4 rounded-card border bg-surface p-4 shadow-soft',
                b.isActive ? 'border-line' : 'border-dashed border-line opacity-70',
              )}
            >
              <button type="button" onClick={() => navigate(`/admin/banners/${b.id}`)}
                className="grid h-16 w-28 shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-brand-700 to-brand-600">
                {b.imageUrl ? (
                  <img src={b.imageUrl} alt="" className="size-full object-cover" />
                ) : (
                  <ImageOff className="size-6 text-white/70" />
                )}
              </button>

              <button type="button" onClick={() => navigate(`/admin/banners/${b.id}`)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'rounded-pill px-1.5 py-0.5 type-overline',
                    b.imageUrl ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'bg-surface-2 text-muted',
                  )}>
                    {b.imageUrl ? (b.showOverlay ? 'Image + text' : 'Image') : 'Text only'}
                  </span>
                </div>
                <div className="mt-1 truncate type-strong text-content">{b.title.replace(/\n/g, ' · ')}</div>
                {b.subtitle && <div className="truncate text-sm text-muted">{b.subtitle}</div>}
              </button>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={b.isActive} onCheckedChange={() => toggleMut.mutate(b)} />
                  <span className={cn('type-label-strong', b.isActive ? 'text-brand-700 dark:text-brand-400' : 'text-subtle')}>
                    {b.isActive ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <Tooltip label="Edit banner">
                  <Button size="sm" variant="default" aria-label="Edit banner" icon={<Pencil />} onClick={() => navigate(`/admin/banners/${b.id}`)} />
                </Tooltip>
                <Button size="sm" variant="text" icon={<Trash2 />} className="text-danger-500! hover:bg-danger-500/10!"
                  onClick={() => setConfirmDelete(b)} aria-label="Delete banner" />
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete banner?"
        width={420}
        footer={
          <>
            <Button variant="default" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="danger" loading={deleteMut.isPending} onClick={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}>Delete</Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          “{confirmDelete?.title.replace(/\n/g, ' ')}” will be deleted. This can’t be undone.
        </p>
      </Modal>
    </div>
  );
}
